var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { defaultSerializeQueryArgs } from '@reduxjs/toolkit/dist/query';
import { paginatedQuery } from 'lib-shared-api-utils';
import unionBy from 'lodash/unionBy';
import moment from 'moment-timezone';
import { api } from 'utils/apis/api';
import { transformReadInstrumentsArg } from './utils/transformArg';
import { transformReadDashboardsResponse, transformReadFiltersResponse, transformReadInstrumentsResponse, } from './utils/transformResponse';
export const analyticsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        readDashboards: builder.query({
            query: () => ({
                version: 1,
                path: 'analytics/dashboards',
            }),
            transformResponse: transformReadDashboardsResponse,
        }),
        readFilters: builder.query({
            query: (_a) => {
                var { id } = _a, body = __rest(_a, ["id"]);
                return ({
                    version: 1,
                    path: `analytics/dashboards/${id}/filters`,
                    method: 'POST',
                    body,
                });
            },
            onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                return __awaiter(this, void 0, void 0, function* () {
                    try {
                        const { data: { data: { filters }, }, } = yield queryFulfilled;
                        // We're adding the filters data to the corresponding dashboard in the `readDashboards`
                        // query state, so that we have easy access to them when working with the dashboard.
                        dispatch(analyticsApi.util.updateQueryData('readDashboards', undefined, (draft) => {
                            const dashboard = draft.data.dashboards.find((d) => d.id === id);
                            if (dashboard) {
                                dashboard.filters = unionBy(filters, dashboard.filters, 'field');
                            }
                        }));
                    }
                    catch (_a) {
                        /* do nothing */
                    }
                });
            },
            transformResponse: transformReadFiltersResponse,
        }),
        readInstruments: builder.query({
            query: (arg) => ({
                version: 1,
                path: `analytics/panels/${arg.id}/instruments`,
                method: 'POST',
                body: transformReadInstrumentsArg(arg),
            }),
            transformResponse: transformReadInstrumentsResponse,
        }),
        readComparisonInstruments: builder.query({
            query: ({ payload }) => ({
                version: 1,
                path: `analytics/panels/${payload.id}/instruments`,
                method: 'POST',
                body: transformReadInstrumentsArg(payload),
            }),
            serializeQueryArgs: ({ queryArgs: { side }, endpointDefinition, endpointName, }) => {
                return defaultSerializeQueryArgs({
                    endpointName,
                    queryArgs: { side },
                    endpointDefinition,
                });
            },
            // Removes the cached data immediately after the last component unsubscribes. This is
            // necessary to ensure that no stale data remains when the user compares different panels.
            keepUnusedDataFor: 0,
        }),
        readPeriodPresets: builder.query({
            query: ({ timezone }) => ({
                version: 1,
                path: `analytics/presets`,
                method: 'POST',
                body: {
                    timezone: timezone !== null && timezone !== void 0 ? timezone : moment.tz.guess(),
                },
            }),
        }),
        readQuickViews: builder.query({
            query: (arg) => paginatedQuery(arg, () => ({
                version: 1,
                path: `analytics/quick-views`,
                params: {
                    type: 'all',
                },
            })),
        }),
        readFavoriteQuickViews: builder.query({
            query: () => paginatedQuery({
                limit: 20,
                offset: 0,
                sort: {
                    key: 'name',
                    direction: 'asc',
                },
            }, () => ({
                version: 1,
                path: `analytics/quick-views`,
                params: {
                    type: 'favorite',
                },
            })),
        }),
        readQuickView: builder.query({
            query: ({ dashboardId, id }) => ({
                version: 1,
                path: `analytics/dashboards/${dashboardId}/quick-views/${id}`,
            }),
        }),
        createQuickView: builder.mutation({
            query: ({ dashboardId, quickView }) => ({
                version: 1,
                path: `analytics/dashboards/${dashboardId}/quick-views`,
                method: 'POST',
                body: Object.assign(Object.assign({}, quickView), { dashboard_id: dashboardId }),
            }),
        }),
        updateQuickView: builder.mutation({
            query: ({ dashboardId, id, quickView }) => ({
                version: 1,
                path: `analytics/dashboards/${dashboardId}/quick-views/${id}`,
                method: 'PATCH',
                body: Object.assign(Object.assign({}, quickView), { dashboard_id: dashboardId }),
            }),
            onQueryStarted({ id, dashboardId, quickView }, { dispatch, queryFulfilled }) {
                return __awaiter(this, void 0, void 0, function* () {
                    const patchReadFavoriteQuickViews = dispatch(analyticsApi.util.updateQueryData('readFavoriteQuickViews', undefined, (draft) => {
                        const mutableQuickViews = draft.data.quick_views;
                        const index = draft.data.quick_views.findIndex((_quickView) => _quickView.id === id);
                        if (index !== -1) {
                            mutableQuickViews[index] = Object.assign(Object.assign({}, mutableQuickViews[index]), quickView);
                        }
                    }));
                    const patchReadQuickView = dispatch(analyticsApi.util.updateQueryData('readQuickView', { id, dashboardId }, (draft) => {
                        const mutableQuickView = draft.data.quick_view;
                        for (const [key, value] of Object.entries(quickView)) {
                            mutableQuickView[key] = value;
                        }
                    }));
                    try {
                        yield queryFulfilled;
                    }
                    catch (_a) {
                        patchReadFavoriteQuickViews.undo();
                        patchReadQuickView.undo();
                    }
                });
            },
        }),
        deleteQuickView: builder.mutation({
            query: ({ dashboardId, id }) => ({
                version: 1,
                path: `analytics/dashboards/${dashboardId}/quick-views/${id}`,
                method: 'DELETE',
            }),
            onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                return __awaiter(this, void 0, void 0, function* () {
                    const patchReadFavoriteQuickViews = dispatch(analyticsApi.util.updateQueryData('readFavoriteQuickViews', undefined, (draft) => {
                        const mutableQuickViews = draft.data.quick_views;
                        const index = draft.data.quick_views.findIndex((_quickView) => _quickView.id === id);
                        if (index !== -1) {
                            mutableQuickViews.splice(index, 1);
                        }
                    }));
                    try {
                        yield queryFulfilled;
                    }
                    catch (_a) {
                        patchReadFavoriteQuickViews.undo();
                    }
                });
            },
        }),
        updateQuickViewFavoriteState: builder.mutation({
            query: ({ quickView, favorited }) => ({
                version: 1,
                path: `analytics/dashboards/${quickView.dashboard_id}/quick-views/${quickView.id}/favorite`,
                method: favorited ? 'POST' : 'DELETE',
            }),
            onQueryStarted({ quickView, favorited }, { dispatch, queryFulfilled }) {
                return __awaiter(this, void 0, void 0, function* () {
                    const patchReadFavoriteQuickViews = dispatch(analyticsApi.util.updateQueryData('readFavoriteQuickViews', undefined, (draft) => {
                        const mutableQuickViews = draft.data.quick_views;
                        if (favorited) {
                            mutableQuickViews.push(Object.assign(Object.assign({}, quickView), { favorited }));
                        }
                        else {
                            mutableQuickViews.splice(mutableQuickViews.findIndex(({ id }) => id === quickView.id), 1);
                        }
                    }));
                    const patchReadQuickView = dispatch(analyticsApi.util.updateQueryData('readQuickView', { id: quickView.id, dashboardId: quickView.dashboard_id }, (draft) => {
                        const mutableQuickView = draft.data.quick_view;
                        mutableQuickView.favorited = favorited;
                    }));
                    try {
                        yield queryFulfilled;
                    }
                    catch (_a) {
                        patchReadFavoriteQuickViews.undo();
                        patchReadQuickView.undo();
                    }
                });
            },
        }),
    }),
});
